import { useState, FormEvent, useRef, KeyboardEvent, ClipboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useCart } from '../../../contexts/CartContext';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

type Step = 'form' | 'verify';
type VerifyChannel = 'email' | 'phone';

function generateCode(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

export default function RegisterPage() {
  const { register } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { totalCount: cartItems, openCart } = useCart();

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Verification
  const [step, setStep] = useState<Step>('form');
  const [channel, setChannel] = useState<VerifyChannel>('email');
  const [generatedCode, setGeneratedCode] = useState('');
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [verifyError, setVerifyError] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showCode, setShowCode] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const strength = password.length === 0 ? 0 : password.length < 6 ? 1 : password.length < 10 ? 2 : 3;
  const strengthLabels = ['', 'Weak', 'Good', 'Strong'];
  const strengthColors = ['', 'bg-red-400', 'bg-yellow-400', 'bg-green-400'];

  const startCooldown = () => {
    setResendCooldown(60);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendCode = (selectedChannel: VerifyChannel) => {
    const code = generateCode();
    setGeneratedCode(code);
    setChannel(selectedChannel);
    setDigits(['', '', '', '', '', '']);
    setVerifyError('');
    setShowCode(false);
    startCooldown();
    return code;
  };

  const handleFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all required fields.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const defaultChannel: VerifyChannel = phone.trim() ? 'phone' : 'email';
    sendCode(defaultChannel);
    setStep('verify');
  };

  const handleDigitChange = (idx: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[idx] = digit;
    setDigits(newDigits);
    setVerifyError('');
    if (digit && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (idx: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const entered = digits.join('');
    if (entered.length < 6) {
      setVerifyError('Please enter the full 6-digit code.');
      return;
    }
    if (entered !== generatedCode) {
      setVerifyError('Incorrect code. Please try again.');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      return;
    }
    setVerifyLoading(true);
    const result = await register(name, email, password, phone);
    setVerifyLoading(false);
    if (result.success) {
      if (cartItems > 0) {
        // Customer had items in cart — bring them back to complete the order
        toast.success(`Welcome, ${name.split(' ')[0]}!`, 'Account created! Your cart is ready — finish your order!');
        navigate('/');
        // Small delay so the page renders before opening the cart
        setTimeout(() => openCart(), 200);
      } else {
        toast.success(`Welcome, ${name.split(' ')[0]}!`, 'Account verified and ready. Time to order some drinks!');
        navigate('/account');
      }
    } else {
      setVerifyError(result.error || 'Registration failed.');
    }
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;
    sendCode(channel);
    toast.info('New code generated', 'Check the code displayed on screen.');
  };

  const destination = channel === 'email' ? email : phone;

  return (
    <div className="min-h-screen bg-[#fafaf8] flex flex-col">
      <Navbar />

      <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-16">
        <div className="w-full max-w-md">

          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img
                src="https://public.readdy.ai/ai/img_res/c5283671-54c4-4b1c-8551-32a9e5d65262.png"
                alt="SipDash"
                className="h-12 w-auto mx-auto"
              />
            </Link>
            {step === 'form' ? (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.04em' }}>
                  Create Account
                </h1>
                <p className="text-gray-500 text-sm">
                  {cartItems > 0
                    ? `Almost there! Create your account to place your order.`
                    : 'Join SipDash Georgetown — fast delivery, great drinks!'}
                </p>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.04em' }}>
                  Verify Your Account
                </h1>
                <p className="text-gray-500 text-sm">Enter the 6-digit code sent to your {channel}</p>
              </>
            )}
          </div>

          {/* Cart reminder banner */}
          {cartItems > 0 && (
            <div className="mb-4 flex items-center gap-3 bg-[#FFF5F0] border border-[#FF6B35]/20 rounded-xl px-4 py-3">
              <div className="w-8 h-8 flex items-center justify-center bg-[#FF6B35] rounded-full flex-shrink-0">
                <i className="ri-shopping-cart-2-line text-white text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">Your cart is waiting!</p>
                <p className="text-xs text-gray-500">
                  {cartItems} item{cartItems !== 1 ? 's' : ''} saved — finish registering to place your order.
                </p>
              </div>
            </div>
          )}

          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {(['form', 'verify'] as Step[]).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  step === s ? 'bg-[#FF6B35] text-white' :
                  (step === 'verify' && s === 'form') ? 'bg-green-500 text-white' :
                  'bg-gray-200 text-gray-400'
                }`}>
                  {step === 'verify' && s === 'form'
                    ? <i className="ri-check-line text-xs" />
                    : i + 1}
                </div>
                <span className={`text-xs font-medium ${step === s ? 'text-gray-900' : 'text-gray-400'}`}>
                  {s === 'form' ? 'Details' : 'Verify'}
                </span>
                {i < 1 && <div className="w-8 h-px bg-gray-200 mx-1" />}
              </div>
            ))}
          </div>

          {/* ── FORM STEP ── */}
          {step === 'form' && (
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              {error && (
                <div className="mb-5 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                  <i className="ri-error-warning-line flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-user-line text-base" />
                    </div>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your full name"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-mail-line text-base" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-phone-line text-base" />
                    </div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+592-600-0000"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-lock-line text-base" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 6 characters"
                      className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={showPassword ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'} />
                    </button>
                  </div>
                  {password.length > 0 && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex gap-1 flex-1">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= strength ? strengthColors[strength] : 'bg-gray-200'}`} />
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">{strengthLabels[strength]}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-lock-check-line text-base" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat your password"
                      className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400"
                    />
                    {confirmPassword.length > 0 && (
                      <div className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center ${confirmPassword === password ? 'text-green-500' : 'text-red-400'}`}>
                        <i className={confirmPassword === password ? 'ri-check-line text-base' : 'ri-close-line text-base'} />
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-semibold py-3 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" /> Sending code...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Continue to Verification
                      <i className="ri-arrow-right-line" />
                    </span>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="text-[#FF6B35] font-medium hover:underline cursor-pointer">
                  Sign in
                </Link>
              </p>
            </div>
          )}

          {/* ── VERIFY STEP ── */}
          {step === 'verify' && (
            <div className="bg-white rounded-2xl p-8 border border-gray-100">

              {/* Channel switcher */}
              {phone.trim() && (
                <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 mb-6">
                  {(['email', 'phone'] as VerifyChannel[]).map((ch) => (
                    <button
                      key={ch}
                      onClick={() => {
                        if (ch !== channel && resendCooldown === 0) {
                          sendCode(ch);
                        }
                      }}
                      disabled={resendCooldown > 0 && ch !== channel}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap ${
                        channel === ch ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700 disabled:opacity-40'
                      }`}
                    >
                      <i className={ch === 'email' ? 'ri-mail-line' : 'ri-smartphone-line'} />
                      {ch === 'email' ? 'Email' : 'Phone'}
                    </button>
                  ))}
                </div>
              )}

              {/* Destination display */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-14 h-14 bg-[#FFF5F0] rounded-full flex items-center justify-center mb-3">
                  <i className={`text-2xl text-[#FF6B35] ${channel === 'email' ? 'ri-mail-send-line' : 'ri-smartphone-line'}`} />
                </div>
                <p className="text-sm text-gray-500">
                  We&apos;ve sent a verification code to
                </p>
                <p className="text-sm font-bold text-gray-900 mt-0.5 break-all">{destination}</p>
              </div>

              {/* Demo notice — code is shown since no real service is connected */}
              <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <div className="flex items-start gap-2">
                  <i className="ri-information-line text-amber-600 text-sm mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-amber-800">Demo Mode</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      No email/SMS service is connected yet. Your code is:
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`font-black text-xl tracking-widest text-amber-900 transition-all ${showCode ? 'blur-0' : 'blur-sm select-none'}`}>
                        {generatedCode}
                      </span>
                      <button
                        onClick={() => setShowCode(!showCode)}
                        className="text-amber-600 hover:text-amber-800 cursor-pointer transition"
                      >
                        <i className={`text-base ${showCode ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* OTP Input */}
              <div className="flex items-center justify-center gap-2 mb-2">
                {digits.map((d, idx) => (
                  <input
                    key={idx}
                    ref={(el) => { inputRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleDigitChange(idx, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(idx, e)}
                    onPaste={idx === 0 ? handlePaste : undefined}
                    className={`w-11 h-13 text-center text-xl font-black border-2 rounded-xl outline-none transition-all ${
                      d
                        ? 'border-[#FF6B35] bg-[#FFF5F0] text-[#FF6B35]'
                        : 'border-gray-200 bg-[#fafaf8] text-gray-900 focus:border-[#FF6B35]'
                    }`}
                    style={{ height: '52px' }}
                  />
                ))}
              </div>

              {verifyError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 mb-4 mt-3">
                  <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0" />
                  <p className="text-xs text-red-600">{verifyError}</p>
                </div>
              )}

              <button
                onClick={handleVerify}
                disabled={verifyLoading || digits.join('').length < 6}
                className="w-full mt-4 bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-semibold py-3 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifyLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" /> Creating account...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-shield-check-line" /> Verify &amp; Create Account
                  </span>
                )}
              </button>

              {/* Resend + Back */}
              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={() => setStep('form')}
                  className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer transition flex items-center gap-1"
                >
                  <i className="ri-arrow-left-line" /> Edit details
                </button>
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  className="text-xs text-[#FF6B35] hover:underline cursor-pointer disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed transition"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </div>
          )}

          <p className="text-center text-xs text-gray-400 mt-4">
            By creating an account you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
